import { AcBadge } from '@/dashboard/ui';
import type { Lead360Input } from '@/dashboard/owner/lead-360-view';
import {
  classMeta,
  formatLeakageAge,
  isKnownBreachClass,
  SLA_CLASS_ORDER,
  type SlaBreachClass,
} from '@/dashboard/sla-leakage/sla-leakage-view';

/**
 * LEAD-INBOX-SLA-AGING-AND-QUEUE-HEALTH-V1 (Refs #315): the Lead 360 SLA breach
 * DETAIL panel (Part B). The list wire (My Work / queue) carries only the
 * compact aging chip; the per-class PICTURE -- which SLAs are breached and by
 * how much, in plain language with the next-step hint -- lives HERE, one click
 * deeper (the 360 law: overview + detail + lawful next step in place).
 *
 * REUSE: reads the EXISTING record-wire `record.slaState` (produced by the ONE
 * V4-P0 classifier via lib/sla-leakage/sla-record-seam.enrichRecordsWithSlaState)
 * and the ONE plain-language copy home (sla-leakage-view.classMeta) -- never a
 * second classifier, never a second label map.
 *
 * FQ POSTURE (recorded per LAW 2 -- PER-SURFACE-ROLE-PROOF): this panel adds NO
 * read and NO new field. `slaState` is enriched at the route (owner-lead-360 /
 * leads-workspace), already audience-gated by that route's existing lens, and
 * is PHI-free by construction (the engine's controlled tokens ONLY -- class /
 * severity / whole waiting-age minutes; no name, phone, note, or clinical
 * field can ride it). The panel therefore INHERITS the record's existing
 * audience -- it never widens a read and is display-only, NEVER authorization.
 *
 * BINDING LAWS honoured here:
 *  - FIVE-SECOND TEST: the badge + each row read in five seconds.
 *  - FIELDS, NOT SENTENCES: each breach is a labeled row (title + waiting-age
 *    field + one-line hint), never comma-spliced prose.
 *  - TRUTHFUL-SOURCE-OR-OMIT: tri-state by optionality -- an ABSENT seam field
 *    (fault fail-safe / legacy producer) renders NOTHING (the record band chip
 *    carries the honest "not available"); a classified-CLEAR record renders
 *    NOTHING (no alarm on a healthy lead); only ACTIVE breaches render.
 *  - PLAIN-LANGUAGE: no snake_case ever reaches the UI; an unknown class token
 *    degrades to a neutral title, never echoed raw.
 */
export interface SlaBreachPanelProps {
  readonly record: Lead360Input;
}

/** Canonical engine order for a breach class; unknown tokens sort last. */
function classRank(cls: string): number {
  const i = SLA_CLASS_ORDER.indexOf(cls as SlaBreachClass);
  return i === -1 ? SLA_CLASS_ORDER.length : i;
}

export default function SlaBreachPanel({
  record,
}: SlaBreachPanelProps): JSX.Element | null {
  const state = record.slaState;
  // Seam absent (not classified) OR classified-clear -> no panel: the record
  // band chip already carries the summary; the detail panel is for ACTIVE
  // breaches only (never a fabricated "clear", never clutter on a healthy lead).
  if (state === undefined || state.breaches.length === 0) {
    return null;
  }

  const ordered = [...state.breaches].sort(
    (a, b) => classRank(a.breachClass) - classRank(b.breachClass),
  );
  const worst = ordered.some((b) => b.severity === 'red') ? 'red' : 'amber';
  const countLabel =
    ordered.length === 1 ? '1 SLA breach' : `${ordered.length} SLA breaches`;

  return (
    <section
      className="lead360-section sla-breach-panel"
      aria-label="SLA status"
      data-testid="sla-breach-panel"
      data-severity={worst}
    >
      <div className="sla-breach-panel__head">
        <AcBadge
          tone={worst === 'red' ? 'danger' : 'pending'}
          testId="sla-breach-badge"
        >
          {countLabel}
        </AcBadge>
      </div>
      <ul className="sla-breach-panel__list" data-testid="sla-breach-list">
        {ordered.map((breach) => {
          const known = isKnownBreachClass(breach.breachClass);
          const title = known ? classMeta(breach.breachClass).title : 'SLA breach';
          const hint = known ? classMeta(breach.breachClass).hint : null;
          return (
            <li
              key={breach.breachClass}
              className="sla-breach-panel__item"
              data-testid="sla-breach-item"
              // Clinical-canary posture: only the KNOWN engine vocabulary rides
              // the DOM; an unknown/foreign token is NEVER echoed raw (even in a
              // data attribute) -- it degrades to 'unknown'.
              data-sla-class={known ? breach.breachClass : 'unknown'}
              data-severity={breach.severity}
            >
              <div className="sla-breach-panel__item-head">
                <span className="sla-breach-panel__item-title">{title}</span>
                <AcBadge
                  tone={breach.severity === 'red' ? 'danger' : 'pending'}
                  testId="sla-breach-item-age"
                >
                  {`Waiting ${formatLeakageAge(breach.ageMinutes)}`}
                </AcBadge>
              </div>
              {/* LEAD-RECORD-POLISH-BATCH-V1 (CC-02): the per-class WINDOW and
                  the overdue delta -- the ONE V4-P0 classifier's own
                  thresholdMinutes, passed through the record seam. This is the
                  DRILL level under the header's Waiting tile: the tile says
                  how long and how overdue overall, these rows say which SLA
                  and by how much. Absent on an older wire -> the row shows the
                  waiting age alone and claims no window. */}
              {typeof breach.thresholdMinutes === 'number' &&
              Number.isFinite(breach.thresholdMinutes) &&
              breach.thresholdMinutes > 0 ? (
                <p
                  className="sla-breach-panel__item-window lead360-muted"
                  data-testid="sla-breach-item-window"
                >
                  {`SLA ${formatLeakageAge(breach.thresholdMinutes)}`}
                  {breach.ageMinutes > breach.thresholdMinutes
                    ? ` - overdue by ${formatLeakageAge(
                        breach.ageMinutes - breach.thresholdMinutes,
                      )}`
                    : ''}
                </p>
              ) : null}
              {hint !== null ? (
                <p className="sla-breach-panel__item-hint">{hint}</p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
